import { Check, ChevronLeft, ChevronRight, Copy, Download, Eye, FileText, LoaderCircle, RefreshCw, Search, X } from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  getAccountProfile,
  getOrderDetail,
  getOrderPdfStatus,
  listOrders,
  PortalApiError,
  requestOrderPdf,
  type OrderDetail,
  type OrderPdfStatus,
  type OrderSummary,
  type Practice
} from "../lib/portalApi";

const DASHBOARD_PDF_READY_RECHECK_DELAYS_MS = [2000, 4000, 7000, 10000, 15000];
const DASHBOARD_ORDERS_PAGE_SIZE = 20;
const HISTORICAL_ORDER_PDF_UNAVAILABLE_MESSAGE = "PDF not available for non-portal submitted orders.";

type OrderPagination = {
  limit: number;
  offset: number;
  returned: number;
  hasPrevious: boolean;
  hasNext: boolean;
};

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function formatDate(value: string | null): string {
  if (!value) return "";
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/);
  if (dateOnlyMatch) {
    return `${dateOnlyMatch[2]}/${dateOnlyMatch[3]}/${dateOnlyMatch[1]}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric"
  }).format(date);
}

function formatDateTime(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

function formatStatus(value: string): string {
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1).toLowerCase())
    .join(" ") || "Submitted";
}

function formatOrderStatus(value: string | null | undefined): string {
  const normalized = value?.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!normalized) return "Submitted";
  if (["ready", "ready_ship", "ready_pickup", "ready_office", "ready_for_pickup"].includes(normalized)) {
    return "Ready for Pickup";
  }
  if (["picked_up"].includes(normalized)) {
    return "Picked Up";
  }
  if (["shipped", "shipped_received", "in_transit", "accepted"].includes(normalized)) {
    return "Shipped";
  }
  if (normalized === "delivered") {
    return "Delivered";
  }
  if (normalized === "filled" || normalized === "dispensed") {
    return "Filled";
  }
  if (["dispensing", "dispense_in_progress", "compounding_queued", "compounding_in_progress", "compounding_inventory_verification", "compounding_inventory_ready", "checked"].includes(normalized)) {
    return "Dispensing";
  }
  if (["cancelled", "canceled", "void", "voided"].includes(normalized)) {
    return "Canceled";
  }
  if (["received", "complete", "completed"].includes(normalized)) {
    return "Received";
  }
  if (["failed", "review_required", "needs_review", "rejected"].includes(normalized)) {
    return "Failed";
  }
  return formatStatus(normalized);
}

function orderDisplayCode(order: Pick<OrderSummary, "orderId" | "orderCode">): string {
  return order.orderCode || order.orderId.slice(0, 8).toUpperCase();
}

function patientDisplayName(order: Pick<OrderSummary, "patientName" | "patientLastName" | "ownerName">): string {
  const name = order.patientName.trim();
  const lastName = order.patientLastName?.trim();
  const ownerLastName = order.ownerName?.trim().split(/\s+/).filter(Boolean).pop();
  if (lastName && ownerLastName && lastName.toLowerCase() === ownerLastName.toLowerCase()) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length > 1 && parts[parts.length - 1].toLowerCase() === lastName.toLowerCase()) {
      return parts.slice(0, -1).join(" ") || parts[0] || "-";
    }
  }
  if (!lastName) return name || "-";
  if (name.toLowerCase().split(/\s+/).includes(lastName.toLowerCase())) return name || lastName;
  return [name, lastName].filter(Boolean).join(" ");
}

function clientDisplayName(order: Pick<OrderSummary, "ownerName">): string {
  return order.ownerName?.trim() || "-";
}

function displayValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function isHistoricalPdfUnavailable(order: Pick<OrderSummary, "sourceType" | "pdfAvailable"> | null | undefined): boolean {
  return order?.sourceType === "historical" && !order.pdfAvailable;
}

function formatCommunicationMethod(value: string | null): string {
  return value ? formatStatus(value) : "-";
}

function formatDeliveryMethod(value: string | null): string {
  const cleaned = value?.trim().toLowerCase();
  if (!cleaned) return "-";
  if (cleaned === "api" || cleaned === "pharmetika_api" || cleaned === "electronic") return "Electronic";
  if (cleaned === "fax" || cleaned === "pharmetika_fax" || cleaned === "notifyre_efax") return "Fax";
  return formatStatus(value || "");
}

function pdfActionErrorMessage(error: unknown): string {
  if (error instanceof PortalApiError) {
    if (error.code === "pdf_unavailable" || error.status === 503 || error.isRetryable) {
      return "PDF could not be prepared right now. Please try Download PDF again in a moment.";
    }
    if (error.isEdgeBlocked) {
      return "PDF download is temporarily unavailable. Please try again in a moment or contact Technology.";
    }
  }
  return "PDF could not be downloaded. Please try again in a moment.";
}

function ordersLoadErrorMessage(error: unknown): string {
  if (error instanceof PortalApiError) {
    if (error.isEdgeBlocked) {
      return "Orders could not be loaded because the request was blocked before it reached the portal. Please try again in a moment. If this continues, contact Technology.";
    }
    if (error.isRetryable || error.code === "network_error") {
      return "Orders could not be loaded because the portal could not reach Companion Pharmacy right now. Please try again in a moment.";
    }
  }
  return "Orders could not be loaded. Refresh the page or try again.";
}

export function DashboardPage() {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState("");
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [orderPageOffset, setOrderPageOffset] = useState(0);
  const [orderPagination, setOrderPagination] = useState<OrderPagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [pdfStatus, setPdfStatus] = useState<OrderPdfStatus | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [detailLoadingOrderId, setDetailLoadingOrderId] = useState<string | null>(null);
  const [pdfLoadingOrderId, setPdfLoadingOrderId] = useState<string | null>(null);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  const [dashboardMessage, setDashboardMessage] = useState<string | null>(null);
  const detailRequestIdRef = useRef(0);

  const orderCountLabel = useMemo(() => {
    if (isLoading) return "Loading orders";
    if (orderPagination && orders.length > 0) {
      const start = orderPagination.offset + 1;
      const end = orderPagination.offset + orders.length;
      return `Showing ${start}-${end}`;
    }
    return `${orders.length} ${orders.length === 1 ? "order" : "orders"}`;
  }, [isLoading, orderPagination, orders.length]);

  async function loadOrders(query = submittedSearch, practiceId = selectedPracticeId, offset = orderPageOffset) {
    const nextOffset = Math.max(0, offset);
    setIsLoading(true);
    setError(null);
    try {
      const result = await listOrders(query, DASHBOARD_ORDERS_PAGE_SIZE, practiceId, nextOffset);
      setOrders(result.orders);
      const pagination = result.pagination ?? {
        limit: DASHBOARD_ORDERS_PAGE_SIZE,
        offset: nextOffset,
        returned: result.orders.length,
        hasPrevious: nextOffset > 0,
        hasNext: false
      };
      setOrderPageOffset(pagination.offset);
      setOrderPagination(pagination);
      setDashboardMessage(result.warnings?.[0] || null);
    } catch (error) {
      setError(ordersLoadErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void loadOrders("");
  }, []);

  useEffect(() => {
    getAccountProfile()
      .then((profile) => {
        setPractices(profile.practices.filter((practice) => practice.status === "active"));
      })
      .catch(() => {
        setPractices([]);
      });
  }, []);

  useEffect(() => {
    const orderId = new URLSearchParams(window.location.search).get("orderId")?.trim();
    if (orderId) void openOrder(orderId);
  }, []);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextSearch = search.trim();
    setSubmittedSearch(nextSearch);
    setOrderPageOffset(0);
    void loadOrders(nextSearch, selectedPracticeId, 0);
  }

  function clearSearch() {
    setSearch("");
    setSubmittedSearch("");
    setOrderPageOffset(0);
    void loadOrders("", selectedPracticeId, 0);
  }

  function handlePracticeFilter(practiceId: string) {
    setSelectedPracticeId(practiceId);
    setOrderPageOffset(0);
    void loadOrders(submittedSearch, practiceId, 0);
  }

  function goToPreviousOrdersPage() {
    const previousOffset = Math.max(orderPageOffset - DASHBOARD_ORDERS_PAGE_SIZE, 0);
    void loadOrders(submittedSearch, selectedPracticeId, previousOffset);
  }

  function goToNextOrdersPage() {
    void loadOrders(submittedSearch, selectedPracticeId, orderPageOffset + DASHBOARD_ORDERS_PAGE_SIZE);
  }

  async function openOrder(orderId: string) {
    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;
    setIsDetailLoading(true);
    setDetailLoadingOrderId(orderId);
    setDetailError(null);
    setDashboardMessage(null);
    setPdfStatus(null);
    setSelectedOrder(null);
    try {
      const [detail, pdf] = await Promise.all([
        getOrderDetail(orderId),
        getOrderPdfStatus(orderId).catch(() => null)
      ]);
      if (detailRequestIdRef.current !== requestId) return;
      setSelectedOrder(detail);
      setPdfStatus(pdf);
    } catch {
      if (detailRequestIdRef.current !== requestId) return;
      setDetailError("Order details could not be loaded. Try again.");
    } finally {
      if (detailRequestIdRef.current === requestId) {
        setIsDetailLoading(false);
        setDetailLoadingOrderId(null);
      }
    }
  }

  async function copyOrderCode(order: OrderSummary) {
    const code = orderDisplayCode(order);
    setDashboardMessage(null);
    try {
      await navigator.clipboard.writeText(code);
      setCopiedOrderId(order.orderId);
      window.setTimeout(() => {
        setCopiedOrderId((current) => (current === order.orderId ? null : current));
      }, 1800);
    } catch {
      setDashboardMessage("Order number could not be copied. Select the order number and copy it manually.");
    }
  }

  async function handlePdfAction(orderId: string, knownStatus: OrderPdfStatus | null = pdfStatus) {
    const order = selectedOrder?.orderId === orderId ? selectedOrder : orders.find((candidate) => candidate.orderId === orderId);
    if (isHistoricalPdfUnavailable(order)) {
      setDashboardMessage(HISTORICAL_ORDER_PDF_UNAVAILABLE_MESSAGE);
      return;
    }

    if (knownStatus?.available && knownStatus.downloadUrl) {
      window.open(knownStatus.downloadUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setIsPdfLoading(true);
    setPdfLoadingOrderId(orderId);
    setDetailError(null);
    setDashboardMessage(null);
    try {
      const status = await getOrderPdfStatus(orderId).catch(() => null);
      if (status?.available && status.downloadUrl) {
        setPdfStatus(status);
        window.open(status.downloadUrl, "_blank", "noopener,noreferrer");
        return;
      }

      const requestedStatus = await requestOrderPdf(orderId);
      setPdfStatus(requestedStatus);

      if (requestedStatus.available && requestedStatus.downloadUrl) {
        window.open(requestedStatus.downloadUrl, "_blank", "noopener,noreferrer");
        return;
      }

      setDashboardMessage("PDF is being prepared. This usually takes a few seconds.");
      for (const delayMs of DASHBOARD_PDF_READY_RECHECK_DELAYS_MS) {
        await wait(delayMs);
        const nextStatus = await getOrderPdfStatus(orderId);
        setPdfStatus(nextStatus);
        if (nextStatus.available && nextStatus.downloadUrl) {
          setDashboardMessage(null);
          window.open(nextStatus.downloadUrl, "_blank", "noopener,noreferrer");
          return;
        }
      }

      setDashboardMessage("PDF is still being prepared. Try Download PDF again in a moment.");
    } catch (error) {
      setDashboardMessage(pdfActionErrorMessage(error));
    } finally {
      setIsPdfLoading(false);
      setPdfLoadingOrderId(null);
    }
  }

  function closeDetail() {
    detailRequestIdRef.current += 1;
    setSelectedOrder(null);
    setPdfStatus(null);
    setDetailError(null);
    setIsDetailLoading(false);
    setDetailLoadingOrderId(null);
  }

  return (
    <section className="settings-section dashboard-section" aria-labelledby="dashboard-title">
      <div className="dashboard-heading">
        <div>
          <p className="eyebrow">Portal Orders</p>
          <h2 id="dashboard-title">Orders</h2>
        </div>
        <span className="status-pill">{orderCountLabel}</span>
      </div>

      <form className="dashboard-toolbar" onSubmit={handleSearch}>
        <label className="dashboard-search">
          <span className="sr-only">Search orders</span>
          <Search size={17} aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search by patient, medication, or order ID"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label className="dashboard-filter">
          <span className="sr-only">Filter by practice</span>
          <select value={selectedPracticeId} onChange={(event) => handlePracticeFilter(event.target.value)}>
            <option value="">All practices</option>
            {practices.map((practice) => (
              <option key={practice.id} value={practice.id}>
                {practice.name}
              </option>
            ))}
          </select>
        </label>
        <button className="secondary-button" type="submit">
          Search
        </button>
        {submittedSearch ? (
          <button className="secondary-button" type="button" onClick={clearSearch}>
            Clear
          </button>
        ) : null}
        <button className="secondary-button icon-label-button" type="button" onClick={() => void loadOrders(submittedSearch, selectedPracticeId, orderPageOffset)}>
          <RefreshCw size={16} aria-hidden="true" />
          Refresh
        </button>
      </form>

      {error ? <div className="form-alert">{error}</div> : null}
      {dashboardMessage ? <div className="form-alert info-alert">{dashboardMessage}</div> : null}

      {isLoading ? (
        <div className="dashboard-loading-panel">
          <div className="context-status loading compact" role="status">
            <LoaderCircle className="spin" size={16} aria-hidden="true" />
            <span>Loading orders...</span>
          </div>
        </div>
      ) : orders.length === 0 ? (
        <div className="dashboard-empty">
          <h3>No orders found</h3>
          <p>
            {submittedSearch
              ? "No orders matched that search."
              : "Submitted prescriptions will appear here once orders are available for this account."}
          </p>
        </div>
      ) : (
        <>
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th scope="col">Order</th>
                  <th scope="col">Patient</th>
                  <th scope="col">Client</th>
                  <th scope="col">Practice</th>
                  <th scope="col">Source</th>
                  <th scope="col">Medication</th>
                  <th scope="col">Status</th>
                  <th scope="col">Expiration</th>
                  <th scope="col">Refills</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.orderId}>
                    <td>
                      <button
                        className="dashboard-order-copy"
                        type="button"
                        onClick={() => void copyOrderCode(order)}
                        aria-label={`Copy order number ${orderDisplayCode(order)}`}
                        title="Copy order number"
                      >
                        <span>{orderDisplayCode(order)}</span>
                        {copiedOrderId === order.orderId ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                      </button>
                      <span className="dashboard-muted">{formatDate(order.orderDate)}</span>
                    </td>
                    <td>{patientDisplayName(order)}</td>
                    <td>{clientDisplayName(order)}</td>
                    <td>{order.practiceName || "-"}</td>
                    <td>{order.sourceLabel || (order.sourceType === "historical" ? "Historical" : "Portal")}</td>
                    <td>{order.drugName}</td>
                    <td>
                      <span className="practice-status active" title={order.statusDefinition || formatOrderStatus(order.status)}>
                        {formatOrderStatus(order.status)}
                      </span>
                    </td>
                    <td>{formatDate(order.expirationDate) || "-"}</td>
                    <td>{order.refillUsage || "-"}</td>
                    <td>
                      <div className="table-action-group">
                        <button
                          className="table-icon-button"
                          type="button"
                          onClick={() => void openOrder(order.orderId)}
                          disabled={detailLoadingOrderId === order.orderId}
                          aria-label={`View order ${orderDisplayCode(order)}`}
                          title="View order details"
                        >
                          <Eye size={17} aria-hidden="true" />
                        </button>
                        <button
                          className="table-icon-button"
                          type="button"
                          onClick={() => void handlePdfAction(order.orderId, null)}
                          disabled={isHistoricalPdfUnavailable(order) || pdfLoadingOrderId === order.orderId}
                          aria-label={`Download PDF for order ${orderDisplayCode(order)}`}
                          title={isHistoricalPdfUnavailable(order) ? HISTORICAL_ORDER_PDF_UNAVAILABLE_MESSAGE : "Download PDF"}
                        >
                          {order.pdfAvailable ? <Download size={17} aria-hidden="true" /> : <FileText size={17} aria-hidden="true" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="dashboard-card-list" aria-label="Orders">
            {orders.map((order) => (
              <article className="dashboard-order-card" key={order.orderId}>
                <div className="dashboard-order-card-header">
                  <div>
                    <button
                      className="dashboard-order-copy"
                      type="button"
                      onClick={() => void copyOrderCode(order)}
                      aria-label={`Copy order number ${orderDisplayCode(order)}`}
                      title="Copy order number"
                    >
                      <span>{orderDisplayCode(order)}</span>
                      {copiedOrderId === order.orderId ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                    </button>
                    <span className="dashboard-muted">{formatDate(order.orderDate)}</span>
                  </div>
                  <span className="practice-status active" title={order.statusDefinition || formatOrderStatus(order.status)}>
                    {formatOrderStatus(order.status)}
                  </span>
                </div>
                <div className="dashboard-order-card-main">
                  <h3>{patientDisplayName(order)}</h3>
                  <p>{order.drugName}</p>
                </div>
                <dl className="dashboard-order-card-meta">
                  <div>
                    <dt>Client</dt>
                    <dd>{clientDisplayName(order)}</dd>
                  </div>
                  <div>
                    <dt>Practice</dt>
                    <dd>{order.practiceName || "-"}</dd>
                  </div>
                  <div>
                    <dt>Source</dt>
                    <dd>{order.sourceLabel || (order.sourceType === "historical" ? "Historical" : "Portal")}</dd>
                  </div>
                  <div>
                    <dt>Expires</dt>
                    <dd>{formatDate(order.expirationDate) || "-"}</dd>
                  </div>
                  <div>
                    <dt>Refills</dt>
                    <dd>{order.refillUsage || "-"}</dd>
                  </div>
                </dl>
                <div className="dashboard-order-card-actions">
                  <button
                    className="secondary-button icon-label-button"
                    type="button"
                    onClick={() => void openOrder(order.orderId)}
                    disabled={detailLoadingOrderId === order.orderId}
                    title="View order details"
                  >
                    <Eye size={16} aria-hidden="true" />
                    View
                  </button>
                  <button
                    className="secondary-button icon-label-button"
                    type="button"
                    onClick={() => void handlePdfAction(order.orderId, null)}
                    disabled={isHistoricalPdfUnavailable(order) || pdfLoadingOrderId === order.orderId}
                    title={isHistoricalPdfUnavailable(order) ? HISTORICAL_ORDER_PDF_UNAVAILABLE_MESSAGE : "Download PDF"}
                  >
                    {order.pdfAvailable ? <Download size={16} aria-hidden="true" /> : <FileText size={16} aria-hidden="true" />}
                    PDF
                  </button>
                </div>
              </article>
            ))}
          </div>
          {orderPagination?.hasPrevious || orderPagination?.hasNext ? (
            <nav className="dashboard-pagination" aria-label="Orders pagination">
              <span className="dashboard-pagination-status">
                Showing {orderPagination.offset + 1}-{orderPagination.offset + orders.length}
              </span>
              <div className="dashboard-pagination-actions">
                {orderPagination.hasPrevious ? (
                  <button className="secondary-button icon-label-button" type="button" onClick={goToPreviousOrdersPage} disabled={isLoading}>
                    <ChevronLeft size={16} aria-hidden="true" />
                    Previous
                  </button>
                ) : null}
                {orderPagination.hasNext ? (
                  <button className="secondary-button icon-label-button" type="button" onClick={goToNextOrdersPage} disabled={isLoading}>
                    Next
                    <ChevronRight size={16} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </nav>
          ) : null}
        </>
      )}

      {detailError ? <div className="form-alert">{detailError}</div> : null}
      {isDetailLoading || selectedOrder ? (
        <div className="order-detail-drawer-shell" role="presentation">
          <button className="order-detail-backdrop" type="button" aria-label="Close order detail" onClick={closeDetail} />
          {isDetailLoading ? (
            <OrderDetailLoadingPanel onClose={closeDetail} />
          ) : selectedOrder ? (
            <OrderDetailPanel
              order={selectedOrder}
              pdfStatus={pdfStatus}
              isPdfLoading={isPdfLoading}
              onClose={closeDetail}
              onPdfAction={() => void handlePdfAction(selectedOrder.orderId)}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function OrderDetailLoadingPanel({ onClose }: { onClose: () => void }) {
  return (
    <aside className="order-detail-panel order-detail-loading-panel" aria-label="Loading order details">
      <div className="order-detail-header">
        <div>
          <p className="eyebrow">Order Details</p>
          <h3>Loading order details</h3>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close order detail">
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <div className="context-status loading compact" role="status">
        <LoaderCircle className="spin" size={16} aria-hidden="true" />
        Loading order details...
      </div>
    </aside>
  );
}

function OrderDetailPanel({
  order,
  pdfStatus,
  isPdfLoading,
  onClose,
  onPdfAction
}: {
  order: OrderDetail;
  pdfStatus: OrderPdfStatus | null;
  isPdfLoading: boolean;
  onClose: () => void;
  onPdfAction: () => void;
}) {
  const pdfReady = pdfStatus?.available && pdfStatus.downloadUrl;
  const pdfUnavailable = isHistoricalPdfUnavailable(order);

  return (
    <aside className="order-detail-panel" aria-labelledby="order-detail-title">
      <div className="order-detail-header">
        <div>
          <p className="eyebrow">Order Detail</p>
          <h3 id="order-detail-title">{orderDisplayCode(order)}</h3>
        </div>
        <button className="icon-button" type="button" aria-label="Close order detail" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      <OrderDetailSection title="Overview">
        <DetailGrid
          items={[
            ["Status", formatOrderStatus(order.status), order.statusDefinition || formatOrderStatus(order.status)],
            ["Order date", formatDate(order.orderDate) || "-"],
            ["Expiration date", formatDate(order.prescription.expirationDate || order.expirationDate) || "-"],
            ["Pharmacy RX #", order.prescription.pharmetikaRxNumber || "-"]
          ]}
        />
      </OrderDetailSection>

      <OrderDetailSection title="Practice association">
        <DetailGrid
          items={[
            ["Practice", order.practice.name || "-"],
            ["Phone", order.practice.phone || "-"],
            ["Address", order.practice.address || "-"]
          ]}
        />
      </OrderDetailSection>

      <OrderDetailSection title="Patient demographics">
        <DetailGrid
          items={[
            ["Patient", order.patient.name || order.patientName],
            ["DOB", formatDate(order.patient.dob) || "-"],
            ["Sex", order.patient.sex || "-"],
            ["Species", order.patient.species || "-"],
            ["Weight", order.patient.weight ? `${order.patient.weight} lb` : "-"]
          ]}
        />
      </OrderDetailSection>

      <OrderDetailSection title="Owner information">
        <DetailGrid
          items={[
            ["Owner", order.owner.name || "-"],
            ["Phone", order.owner.phone || "-"],
            ["Email", order.owner.email || "-"],
            ["Address", order.owner.address.display || "-"]
          ]}
        />
      </OrderDetailSection>

      <OrderDetailSection title="Prescription">
        <DetailGrid
          items={[
            ["Medication", order.drugName],
            ["Strength", order.prescription.strength || "-"],
            ["Dosage form", order.prescription.dosageForm || "-"],
            ["Quantity", order.prescription.quantity || "-"],
            ["Refills", order.prescription.prn ? "PRN" : displayValue(order.prescription.refills)],
            ["Communication", formatCommunicationMethod(order.prescription.communicationMethod)],
            ["Delivery", formatDeliveryMethod(order.prescription.deliveryMechanism)],
            ["Directions", order.prescription.directions || "-"],
            ["Notes", order.prescription.notes || "-"]
          ]}
        />
      </OrderDetailSection>

      <OrderDetailSection title="Signature">
        <DetailGrid
          items={[
            ["Signature", order.signature.mode ? formatStatus(order.signature.mode) : "-"],
            ["Signature date", formatDateTime(order.signature.capturedAt) || "-"]
          ]}
        />
      </OrderDetailSection>

      <details className="order-detail-section order-transaction-section" open>
        <summary>
          <span>Transaction history</span>
          <span>{order.transactionHistory.length} {order.transactionHistory.length === 1 ? "event" : "events"}</span>
        </summary>
        {order.transactionHistory.length ? (
          <ol className="order-transaction-list">
            {order.transactionHistory.map((event) => (
              <li key={event.id}>
                <div>
                  <strong>{event.label}</strong>
                  <span title={event.statusDefinition || (event.status ? formatOrderStatus(event.status) : "Status pending")}>
                    {event.status ? formatOrderStatus(event.status) : "Status pending"}
                  </span>
                </div>
                <time>{formatDateTime(event.occurredAt) || "-"}</time>
                {event.detail ? <p>{event.detail}</p> : null}
              </li>
            ))}
          </ol>
        ) : (
          <p className="dashboard-muted">No transaction events are available yet.</p>
        )}
      </details>

      <div className="order-detail-actions">
        <span className="status-pill">
          {pdfReady ? "PDF ready" : pdfUnavailable ? "PDF unavailable" : pdfStatus?.status === "queued" ? "PDF queued" : "PDF pending"}
        </span>
        <button
          className="secondary-button icon-label-button"
          type="button"
          onClick={onPdfAction}
          disabled={isPdfLoading || pdfUnavailable}
          title={pdfUnavailable ? HISTORICAL_ORDER_PDF_UNAVAILABLE_MESSAGE : pdfReady ? "Download PDF" : "Generate PDF"}
        >
          {pdfReady ? <Download size={16} aria-hidden="true" /> : <FileText size={16} aria-hidden="true" />}
          {isPdfLoading ? "Working..." : pdfReady ? "Download PDF" : pdfUnavailable ? "PDF unavailable" : "Generate PDF"}
        </button>
      </div>
    </aside>
  );
}

function OrderDetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="order-detail-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

function DetailGrid({ items }: { items: Array<[string, string | number, string?]> }) {
  return (
    <dl className="order-detail-grid">
      {items.map(([label, value, title]) => (
        <div key={label} className={String(value).length > 34 ? "span-2" : undefined}>
          <dt>{label}</dt>
          <dd title={title}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
